import React, { ReactNode, useCallback, useEffect } from 'react';
import { useStepEditorContext } from 'src/store/step-editor';

import { EditContentFormContentPreview } from 'src/editor/content-form/content-preview';
import InnerHTML from 'dangerously-set-html-content';
import FilePreviewBox from 'src/common/FilePreviewBox';
import { JourneyEditorPreview } from 'src/dashboard/JourneyEditorPreview';
import {
  CONTENT_TYPE_FILE,
  CONTENT_TYPE_EMBED,
  CONTENT_TYPE_EXTERNAL_LINK,
  CONTENT_TYPE_IMAGE,
  CONTENT_TYPE_PDF,
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_JOURNEY_DOCUMENT,
} from './constants';
import FilePreview from 'src/common/FilePreview';
import StepEditorExternalLink from './step-editor/preview/external-link';
import StepEditorExternalFile from './step-editor/preview/external-file';
import { useEditorContext } from 'src/store/editor';

export const useContentPreviewGenerator = () => {
  const {
    previewObject,
    previewNode,
    previewVideoHTML,
    setPreviewObject,
    contentType,
    name,
    url,
    transformed_url,
    embed_code,
    link,
    uploadedFile,
    uploadedS3Asset,
  } = useStepEditorContext();

  const { journey } = useEditorContext();

  useEffect(() => {
    if (contentType === CONTENT_TYPE_FILE && uploadedS3Asset) {
      setPreviewObject({
        externalFile: uploadedS3Asset,
      });
    } else if (contentType === CONTENT_TYPE_EXTERNAL_LINK && url && link) {
      setPreviewObject({
        externalLink: {
          ...link,
          name: name,
          url: url,
          title: link.title,
          description: link.description,
          cta: link.cta,
          meta_image_url: link.meta_image_url,
          hide_image: link.hide_image,
        },
      });
    } else if (contentType === CONTENT_TYPE_EMBED && url && transformed_url) {
      setPreviewObject({
        iframeSrc: transformed_url,
      });
    } else if (contentType === CONTENT_TYPE_EMBED && embed_code) {
      setPreviewObject({
        embedCode: embed_code,
      });
    } else if (
      [CONTENT_TYPE_VIDEO, CONTENT_TYPE_IMAGE, CONTENT_TYPE_PDF, CONTENT_TYPE_FILE].includes(contentType) &&
      uploadedFile
    ) {
      setPreviewObject({ file: uploadedFile });
    } else if (
      [CONTENT_TYPE_VIDEO, CONTENT_TYPE_IMAGE, CONTENT_TYPE_PDF, CONTENT_TYPE_FILE, CONTENT_TYPE_JOURNEY_DOCUMENT].includes(contentType) &&
      previewNode
    ) {
      setPreviewObject({ node: previewNode });
    } else if (contentType === CONTENT_TYPE_VIDEO && uploadedS3Asset) {
      setPreviewObject({
        videoSrc: uploadedS3Asset.url,
      });
    } else if (previewVideoHTML) {
      setPreviewObject({ streamHTML: previewVideoHTML });
    } else {
      setPreviewObject(null);
    }
  }, [
    contentType,
    name,
    url,
    transformed_url,
    embed_code,
    link,
    uploadedFile,
    uploadedS3Asset,
    previewNode,
    previewVideoHTML,
    setPreviewObject,
  ]);

  const renderContentPreview = useCallback(
    (isMobileForced = false) => {
      let preview: ReactNode = null;

      if (previewObject) {
        if ('iframeSrc' in previewObject) {
          preview = <iframe title='Content Preview' src={previewObject.iframeSrc} width='100%' height='100%' />;
        } else if ('node' in previewObject) {
          preview = <EditContentFormContentPreview content={previewObject.node} journey={journey} isMobileForced={isMobileForced} />;
        } else if ('embedCode' in previewObject) {
          preview = <InnerHTML html={previewObject.embedCode} />;
        } else if ('streamHTML' in previewObject) {
          preview = previewObject.streamHTML;
        } else if ('file' in previewObject) {
          const file = previewObject['file'];
          const params = previewObject['params'] || {};
          if (file) {
            preview = (
              <FilePreviewBox url={URL.createObjectURL(file)} type={file.type} name={file.name} params={params} />
            );
          }
        } else if ('videoSrc' in previewObject) {
          preview = <FilePreview url={previewObject.videoSrc} type='video/mp4' name='video.mp4' preload='metadata' />;
        } else if ('externalLink' in previewObject) {
          preview = <StepEditorExternalLink />;
        } else if ('externalFile' in previewObject) {
          preview = <StepEditorExternalFile />;
        }
      }

      return <JourneyEditorPreview key='content-form-preview'>{preview}</JourneyEditorPreview>;
    },
    [previewObject]
  );

  return {
    renderContentPreview,
  };
};
